import cn from "classnames";
import type { CSSProperties } from "react";

import { Text } from "@/components/text/text.component";

import { FaqSection as FaqSectionType } from "@/sanity.types";

import { FaqAccordionList, type FaqList } from "./faqAccordionList.component";

export const FaqSection = ({ block, pageType }: FaqSectionProps) => {
  const faqGroups = toFaqGroups(block);
  if (!faqGroups.length) return null;

  const layout = (block as FaqSectionBlock).layout ?? "standard";
  const isCenteredLayout = layout === "centered";
  const isMonitoringPage = pageType === "monitoringPage";
  const customPadding = customPaddingVariables(
    block,
    DEFAULT_PADDING[isCenteredLayout ? "centered" : "standard"],
  );

  return (
    <section
      aria-label="Frequently asked questions"
      style={customPadding}
      className={cn(
        "flex w-full flex-col",
        customPadding
          ? "px-(--faq-px) py-(--faq-py) md:px-(--md-faq-px) md:py-(--md-faq-py) lg:px-(--lg-faq-px) lg:py-(--lg-faq-py)"
          : {
              "px-5 py-10 md:px-0": !isCenteredLayout,
              "px-6 py-8 md:px-10 md:py-12 lg:py-14": isCenteredLayout,
            },
        {
          "bg-primary text-greyscale-body":
            !isCenteredLayout && block.theme === "primary",
          "bg-secondary text-white":
            !isCenteredLayout && block.theme === "secondary",
          "bg-secondary-100 text-white":
            !isCenteredLayout && block.theme === "secondary100",
          "bg-greyscale-background text-greyscale-body":
            isCenteredLayout || block.theme === "light",
        },
        !isCenteredLayout && block.theme,
      )}
      data-testid="faq-section">
      <div
        className={cn("mx-auto w-full", {
          container: !isCenteredLayout,
          "max-w-[1090px]": isCenteredLayout,
        })}>
        <Text
          variant={isCenteredLayout ? "preHeaderSm" : "preHeaderLg"}
          className={cn("text-center uppercase", {
            "text-greyscale-headline tracking-[0.02em]": isCenteredLayout,
            "text-greyscale-headline": isMonitoringPage && !isCenteredLayout,
          })}
          id="faq-section-title">
          {block.title}
        </Text>
        {block.subtitle && (
          <Text
            variant={isCenteredLayout ? "h5" : "h3"}
            component={isCenteredLayout ? "h2" : "h3"}
            id="faq-section-subtitle"
            className={cn("mt-2 text-center", {
              "text-greyscale-headline mx-auto max-w-[760px]": isCenteredLayout,
              "text-greyscale-headline": isMonitoringPage && !isCenteredLayout,
            })}>
            {block.subtitle}
          </Text>
        )}
        <div
          className={cn("flex flex-col", {
            "mt-12": !isCenteredLayout,
            "mt-7 md:mt-9": isCenteredLayout,
          })}>
          <FaqAccordionList
            groups={faqGroups}
            theme={block.theme}
            isCenteredLayout={isCenteredLayout}
            isMonitoringPage={isMonitoringPage}
          />
        </div>
      </div>
    </section>
  );
};

/** The section's own FAQ items come first, followed by each FAQ group under its heading. */
const toFaqGroups = (block: FaqSectionType | undefined): FaqList[] => {
  const groups: FaqList[] = block?.faqs?.length
    ? [{ key: "faqs", faqs: block.faqs }]
    : [];
  block?.groups?.forEach((group) => {
    if (group.faqs?.length) {
      groups.push({
        key: group._key,
        heading: group.heading,
        faqs: group.faqs,
      });
    }
  });
  return groups;
};

type Padding = FaqSectionType["padding"];

const DEFAULT_PADDING = {
  standard: {
    mobile: { paddingY: 40, paddingX: 20 },
    tablet: { paddingY: 40, paddingX: 0 },
    desktop: { paddingY: 40, paddingX: 0 },
  },
  centered: {
    mobile: { paddingY: 32, paddingX: 24 },
    tablet: { paddingY: 48, paddingX: 40 },
    desktop: { paddingY: 56, paddingX: 40 },
  },
};

/** CSS variables for the editor's padding, or nothing when none is set so the layout's own classes apply. */
const customPaddingVariables = (
  block: FaqSectionType,
  defaults: (typeof DEFAULT_PADDING)["standard"],
) => {
  const screens: [string, Padding, { paddingY: number; paddingX: number }][] = [
    ["", block.mobilePadding, defaults.mobile],
    ["md-", block.tabletPadding, defaults.tablet],
    ["lg-", block.padding, defaults.desktop],
  ];
  const hasCustomPadding = screens.some(
    ([, padding]) => padding?.paddingY != null || padding?.paddingX != null,
  );
  if (!hasCustomPadding) return undefined;

  return Object.fromEntries(
    screens.flatMap(([prefix, padding, fallback]) => [
      [`--${prefix}faq-py`, `${padding?.paddingY ?? fallback.paddingY}px`],
      [`--${prefix}faq-px`, `${padding?.paddingX ?? fallback.paddingX}px`],
    ]),
  ) as CSSProperties;
};

type FaqSectionProps = {
  block: FaqSectionType;
  pageType?: string;
};

type FaqSectionBlock = FaqSectionType & {
  layout?: "standard" | "centered";
};
